import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaMusic, FaPlay } from 'react-icons/fa';
import { musicAPI } from '../utils/api';
import Sidebar from '../components/Sidebar';
import MusicTag from '../components/MusicTag';

const SongPosts = () => {
  const { songId } = useParams();
  const [data, setData] = useState({ posts: [], song: null, total: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [songId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await musicAPI.getPostsBySong(songId);
      setData(res.data);
    } catch {
      console.error('Failed to load song posts');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  const song = data.song;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />
      <main className="flex-1 ml-64 p-6" data-testid="song-posts-page">
        <div className="max-w-2xl mx-auto">
          {/* Back button */}
          <button onClick={() => navigate(-1)} className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6" data-testid="back-btn">
            <FaArrowLeft />
            <span className="text-sm font-medium">Back</span>
          </button>

          {/* Song header */}
          {song && (
            <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6" data-testid="song-header">
              <div className="flex items-center space-x-5">
                <div className="relative w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 shadow-lg">
                  <img src={song.album_art} alt={song.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                      <FaPlay className="text-[#4A90E2] text-xs ml-0.5" />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <FaMusic className="text-[#4A90E2] text-sm" />
                    <h1 className="text-xl font-bold text-gray-900">{song.title}</h1>
                  </div>
                  <p className="text-gray-600">{song.artist}</p>
                  <div className="flex items-center space-x-3 mt-2">
                    {song.album && <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{song.album}</span>}
                    {song.genre && <span className="text-xs text-[#4A90E2] bg-[#4A90E2]/10 px-2 py-0.5 rounded">{song.genre}</span>}
                    <img src="/gdme-logo.svg" alt="GDME" className="h-3.5" />
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  <span className="font-semibold text-gray-900">{data.total}</span> {data.total === 1 ? 'post' : 'posts'} using this song
                </p>
              </div>
            </div>
          )}

          {/* Posts */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => <div key={i} className="bg-gray-100 rounded-2xl h-32 animate-pulse" />)}
            </div>
          ) : data.posts.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center">
              <p className="text-gray-500">No posts with this song yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {data.posts.map((post) => (
                <div key={post.id} className="bg-white border border-gray-200 rounded-2xl p-5" data-testid={`song-post-${post.id}`}>
                  <div className="flex items-center space-x-3 mb-3">
                    <Link to={`/profile/${post.username}`}>
                      <img src={post.avatar_url || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg'} alt={post.full_name} className="w-10 h-10 rounded-full object-cover" />
                    </Link>
                    <div>
                      <Link to={`/profile/${post.username}`} className="font-bold text-gray-900 hover:underline text-sm">{post.full_name}</Link>
                      <p className="text-xs text-gray-500">@{post.username} &middot; {formatTimeAgo(post.created_at)}</p>
                    </div>
                  </div>
                  <p className="text-gray-800 mb-3">{post.content}</p>
                  {post.media_url && (
                    <div className="rounded-xl overflow-hidden mb-3">
                      {post.media_type?.startsWith('image') ? (
                        <img src={post.media_url} alt="" className="w-full max-h-80 object-cover" />
                      ) : post.media_type?.startsWith('video') ? (
                        <video src={post.media_url} controls className="w-full max-h-80" />
                      ) : null}
                    </div>
                  )}
                  {post.music && <MusicTag music={post.music} />}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SongPosts;
